import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import FlexWrapper, { AlignItems } from "../containers/FlexWrapper";
import Spacing from "../containers/Spacing";
import HelpIcon from "../icons/HelpIcon";
import RequiredMarker from "../text/RequiredMarker";
import Text, { TextVariant } from "../text/Text";
import Tooltip, { TooltipPosition } from "../tooltip/Tooltip";

export enum RadioInputVariant {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
}

export enum RadioInputSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
}

export interface RadioInputProps {
  variant?: RadioInputVariant;
  size?: RadioInputSize;
  isSelected: boolean;
  label?: string;
  labelTooltip?: string | React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
  onChange: (selected: boolean) => void;
}

const RadioContainer = styled.div<{ $isDisabled: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
`;

const RadioWrapper = withTheme(styled.div<
  PropsWithTheme<{
    $variant: RadioInputVariant;
    $size: RadioInputSize;
    $isSelected: boolean;
    $isDisabled: boolean;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ $size }) => {
    return match($size)
      .with(RadioInputSize.SMALL, () => "12px")
      .with(RadioInputSize.MEDIUM, () => "16px")
      .exhaustive();
  }};
  height: ${({ $size }) => {
    return match($size)
      .with(RadioInputSize.SMALL, () => "12px")
      .with(RadioInputSize.MEDIUM, () => "16px")
      .exhaustive();
  }};
  border-radius: 50%;
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};

  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.color.background.primaryAlt : theme.color.background.tertiary};

  transition:
    border-color 0.075s ease-in-out,
    background-color 0.075s ease-in-out;

  &:hover {
    border-color: ${({ theme, $isDisabled, $variant }) => {
      return match($variant)
        .with(RadioInputVariant.PRIMARY, () =>
          $isDisabled ? theme.color.border.primary : theme.color.border.primary,
        )
        .with(RadioInputVariant.SECONDARY, () =>
          $isDisabled ? theme.color.border.primary : theme.color.border.primary,
        )
        .exhaustive();
    }};
  }
`);

const RadioDot = withTheme(styled.div<
  PropsWithTheme<{
    $size: RadioInputSize;
    $isSelected: boolean;
  }>
>`
  width: ${({ $size }) => {
    return match($size)
      .with(RadioInputSize.SMALL, () => "6px")
      .with(RadioInputSize.MEDIUM, () => "8px")
      .exhaustive();
  }};
  height: ${({ $size }) => {
    return match($size)
      .with(RadioInputSize.SMALL, () => "6px")
      .with(RadioInputSize.MEDIUM, () => "8px")
      .exhaustive();
  }};
  border-radius: 50%;

  background-color: ${({ theme }) => theme.color.background.primary};

  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0)};
  transform: ${({ $isSelected }) => ($isSelected ? "scale(1)" : "scale(0)")};

  transition:
    opacity 0.075s ease-in-out,
    transform 0.075s ease-in-out;
`);

const RadioInput = ({
  variant = RadioInputVariant.PRIMARY,
  size = RadioInputSize.MEDIUM,
  isSelected,
  label,
  labelTooltip,
  isRequired = false,
  isDisabled = false,
  onChange,
}: RadioInputProps) => {
  const handleClick = () => {
    if (!isDisabled) {
      onChange(!isSelected);
    }
  };

  return (
    <RadioContainer $isDisabled={isDisabled} onClick={handleClick}>
      <RadioWrapper
        $variant={variant}
        $size={size}
        $isSelected={isSelected}
        $isDisabled={isDisabled}
      >
        <RadioDot $size={size} $isSelected={isSelected} />
      </RadioWrapper>
      {label && (
        <FlexWrapper alignItems={AlignItems.CENTER} gap={1}>
          <Text variant={TextVariant.PRIMARY}>{label}</Text>
          {isRequired && <RequiredMarker />}
          {labelTooltip && (
            <Spacing left={4}>
              <Tooltip body={labelTooltip} position={TooltipPosition.RIGHT}>
                <HelpIcon />
              </Tooltip>
            </Spacing>
          )}
        </FlexWrapper>
      )}
    </RadioContainer>
  );
};

export default RadioInput;
